/* eslint-disable react/jsx-no-bind */
import { Button } from '@cannect/new-components/atoms'
import ConfirmationDialogue from 'components/ConfirmationDialogue'
import Loading from 'components/Loading'
import { Fragment, ReactNode, useEffect, useState } from 'react'
import { FiShoppingCart } from 'react-icons/fi'
import { LuPackage } from 'react-icons/lu'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import api from 'services/api'
import DocumentsListModal from './DocumentsListModal'
import PasswordModal from './PasswordModal'
import RecipeProduct from './RecipeProduct'
import * as Style from './styles'

export type BaseRecipePathParams = {
  token: string
}

export type BaseRecipeProduct = {
  id: number
  translated_name: string
  price: number
  promotional_price?: number
  prescription: string
  required_bottles?: number
  parent_product_id?: number | null
  is_combo?: boolean
  media?: { path: string; format: string } | { path: string; format: string }[] | null
  shape_name?: string | null
  class_id?: number | null
  supplier_id?: number | null
  origin?: string | null
}

type FetchBaseRecipeResponse = {
  recipe: BaseRecipeProduct[]
}

export type RecipeDocumentType = 'simple' | 'exam' | 'rce' | 'antimicrobial'

export type RecipeDocument = {
  type: RecipeDocumentType
  token: string
  url: string
  label: string
  verification_code?: string
}

export type FetchRecipeResponse = {
  recipe_link: string
  success: boolean
  link_merged_pdf?: string
  documents?: RecipeDocument[]
}

export type DialogueReason = 'shareRecipe' | 'downloadRecipe'

export default function BaseRecipe() {
  const { token } = useParams<BaseRecipePathParams>()
  const history = useHistory()
  const [recipe, setRecipe] = useState<BaseRecipeProduct[]>([])
  const [linkToDownloadRecipe, setLinkToDownloadRecipe] = useState('')
  const [loading, setLoading] = useState(true)
  const [isErrorDialogueOpen, setIsErrorDialogueOpen] = useState(false)
  const [passwordModalComponent, setPasswordModalComponent] = useState<ReactNode>()
  const [fetchedDocuments, setFetchedDocuments] = useState<RecipeDocument[]>([])
  const [isDocumentsModalOpen, setIsDocumentsModalOpen] = useState(false)

  useEffect(() => {
    async function fetchBaseRecipe() {
      try {
        const response = await api.get<FetchBaseRecipeResponse>(`baserecipe/${token}`)
        setRecipe(response.data.recipe)
      } catch (err) {
        console.error(err)
      } finally {
        setLoading(false)
      }
    }
    fetchBaseRecipe()
  }, [])

  function handleOpenDownloadModal(dialogueReason: DialogueReason) {
    if (dialogueReason === 'downloadRecipe' && fetchedDocuments.length !== 0) {
      setIsDocumentsModalOpen(true)
      return
    }

    if (linkToDownloadRecipe.length !== 0) {
      if (dialogueReason === 'downloadRecipe') {
        handleDownloadPdf(linkToDownloadRecipe)
      } else {
        handleOpenCart()
      }
      return
    }

    setPasswordModalComponent(
      <PasswordModal
        isOpen
        onRequestClose={() => setPasswordModalComponent(undefined)}
        setLoading={setLoading}
        dialogueReason={dialogueReason}
        token={token}
        onRecipeFetch={onFetchRecipe}
      />
    )
  }

  async function onFetchRecipe(dialogueReason: DialogueReason, fetchResult?: FetchRecipeResponse) {
    if (!fetchResult) {
      setIsErrorDialogueOpen(true)
      return
    }

    if (dialogueReason === 'downloadRecipe') {
      if (fetchResult.documents && fetchResult.documents.length > 0) {
        setFetchedDocuments(fetchResult.documents)
        setIsDocumentsModalOpen(true)
      } else {
        handleDownloadPdf(fetchResult.link_merged_pdf || fetchResult.recipe_link)
      }
    }
    setLinkToDownloadRecipe(fetchResult.link_merged_pdf || fetchResult.recipe_link)
    setPasswordModalComponent(undefined)
  }

  function handleDownloadPdf(linkToDownload: string) {
    const anchorElement = document.createElement('a')
    anchorElement.href = linkToDownload
    anchorElement.target = '_blank'
    anchorElement.click()
  }

  async function handleOpenCart() {
    try {
      setLoading(true)
      const { data } = await api.post(`/order_recipe/${token}`)

      if (!data?.linkCart) {
        toast.error('Não foi possível abrir o carrinho. Tente novamente.')
        return
      }

      window.location.href = data.linkCart
    } catch (error) {
      toast.error('Não foi possível abrir o carrinho. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Style.BaseRecipeContainer>
      <Style.ContentWrapper>
        {recipe.length !== 0 ? (
          <>
            <Style.SecondHeader>
              <span>{token}</span>
              <Button onClick={() => handleOpenDownloadModal('downloadRecipe')}>Baixar PDF</Button>
            </Style.SecondHeader>
            <Style.RecipesWrapper>
              {recipe
                .filter((product) => !product.parent_product_id)
                .map((product, index, filtered) => {
                  const comboChildren = product.is_combo ? recipe.filter((p) => p.parent_product_id === product.id) : []

                  return (
                    <Fragment key={product.id}>
                      <RecipeProduct product={product} />
                      {product.is_combo && comboChildren.length > 0 && (
                        <div className="my-2 overflow-hidden rounded-xl border border-gray-100 bg-gray-50">
                          <div className="flex items-center gap-2 border-b border-gray-100 bg-gray-100 px-4 py-2.5">
                            <LuPackage size={15} className="text-gray-500" />
                            <span className="text-xs font-semibold text-gray-700">
                              Combo &middot; {comboChildren.length} formulações
                            </span>
                          </div>
                          <div className="flex flex-col gap-2 p-4">
                            {comboChildren.map((child) => (
                              <div
                                key={child.id}
                                className="flex flex-col gap-1 rounded-lg border border-gray-100 bg-white px-3 py-2.5">
                                <span className="text-sm font-semibold text-gray-950">{child.translated_name}</span>
                                {child.prescription && (
                                  <span className="text-xs text-gray-500">{child.prescription}</span>
                                )}
                                {child.required_bottles != null && Number(child.required_bottles) > 0 && (
                                  <span className="w-fit rounded bg-gray-100 px-2 py-0.5 text-[11px] text-gray-500">
                                    {child.required_bottles}{' '}
                                    {Number(child.required_bottles) === 1 ? 'frasco' : 'frascos'}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {index !== filtered.length - 1 && <hr />}
                    </Fragment>
                  )
                })}
            </Style.RecipesWrapper>
          </>
        ) : (
          <p>Não foi possível localizar a receita informada</p>
        )}
        <Button
          variant="outline_light"
          className="w-[200px] self-center"
          onClick={() => history.push(`/receitas/${token}/ajuda`)}>
          Precisa de ajuda?
        </Button>
        <Button className="w-[300px] self-center" onClick={() => handleOpenCart()}>
          Comprar produtos <FiShoppingCart size={20} />
        </Button>
      </Style.ContentWrapper>

      {loading && <Loading loading={loading} />}
      {passwordModalComponent}
      {isDocumentsModalOpen && (
        <DocumentsListModal
          isOpen={isDocumentsModalOpen}
          documents={fetchedDocuments}
          onRequestClose={() => setIsDocumentsModalOpen(false)}
        />
      )}
      {isErrorDialogueOpen && (
        <ConfirmationDialogue
          isOpened={isErrorDialogueOpen}
          onRequestClose={() => setIsErrorDialogueOpen(false)}
          onConfirmationClick={() => setIsErrorDialogueOpen(false)}
          title="Houve um erro ao fazer o download PDF da receita!"
          description="A download do PDF não pôde ser feito devido a um erro interno. Tente novamente mais tarde."
          confirmationButtonText="Entendi!"
          confirmationOnlyDialogue
        />
      )}
    </Style.BaseRecipeContainer>
  )
}
